import { FC, useEffect, useState } from 'react';
import styles from './ColorRampField.module.scss';
import Swatch from './Swatch';
import ViewOnlySwatch from './Swatch/ViewOnly';
import { Field, useFormikContext } from 'formik';
import colorMethods from '../../utils/color';
import Button from '../GroupActions/Button';
import base_colors from '../../data/base_colors.json';
import ToggleGroup from '../ToggleGroup';
import MultiSelectField from '../Inputs/MultiSelectField';
import { ColorRampProvider, useColorRamp } from './ColorRampContext';

export interface ColorRampFieldProps {
  rootName: string;
  removeRamp: any;
  addRamp: any;
  id: string;
}

export interface ColorRampActionsProps {
  id: string;
  swatchCount: number;
}

const generateColors = (color: Record<string, any>, callback: any) => {
  const hex =
    color.hsl && color.hsl.length === 3 && color.hsl[0] !== 0
      ? colorMethods.hslToHex(color.hsl[0], color.hsl[1], color.hsl[2])
      : color.hex;
  const rgbArrRamp = [].concat(
    // @ts-ignore
    colorMethods.interpolateColors('#ffffff', hex, 7).slice(1, 6),
    colorMethods.interpolateColors(hex, '#000000', 6).slice(0, 5)
  );

  const formatted = rgbArrRamp.map((rgb) => {
    return {
      hex: colorMethods.rgbArrayToHex(rgb),
      hsl: colorMethods.rgbArrayToHsl(rgb),
      rgb,
      contrastText: '',
      contrastUi: '',
    };
  });
  callback('colors', formatted);
};

const ColorRampActions: FC<ColorRampActionsProps> = ({ id, swatchCount }) => {
  // @ts-ignore
  const { setShowContrast, setThemeType, setRampId } = useColorRamp();

  useEffect(() => {
    setRampId(id);
  }, [id]);

  const handleThemeChange = (v: any) => {
    // Use this if multiple options are allowed
    // setThemeType(v.map((item: any) => item.value));

    setThemeType(v.value);
  };

  return (
    <div className={styles.ramp__actions__secondary}>
      <MultiSelectField
        name='theme'
        label='Theme Type'
        onChange={handleThemeChange}
        options={[
          { label: 'Light Theme', value: 'light_theme' },
          { label: 'Dark Mode', value: 'dark_mode' },
          { label: 'Dark Theme', value: 'dark_theme' },
        ]}
      />
      <ToggleGroup
        disabled={swatchCount < 2}
        options={[
          { name: `showTextContrast-${id}`, label: 'Show Text Contrast Colors' },
          { name: `showUiContrast-${id}`, label: 'Show UI Contrast Colors' },
        ]}
        connectValue={(t: string) => setShowContrast(t)}
      />
    </div>
  );
};

export const ColorRampField: FC<ColorRampFieldProps> = ({ rootName, removeRamp, addRamp, id }) => {
  // const [showContrast, setShowContrast] = useState<string | null>(null);
  // const [themeType, setThemeType] = useState<string[] | null>(null);
  const { values, setFieldValue } = useFormikContext();

  // @ts-ignore
  const rampColors = values[rootName];

  return (
    <ColorRampProvider>
      <div className={styles.ramp}>
        <div className={styles.ramp__actions}>
          <div className={styles.ramp__actions__primary}>
            <label className='formControl__label' htmlFor='name'>
              Ramp Title
            </label>
            <Field className='formControl__input' name='name' type='text' />
          </div>

          <ColorRampActions id={id} swatchCount={rampColors.length} />
        </div>
        <div className={styles.ramp__window}>
          {rampColors && (
            <>
              {rampColors.length > 1 && (
                <>
                  <ViewOnlySwatch
                    color={base_colors.black}
                    colors={rampColors}
                    // showTextContrast={showContrast === `showTextContrast-${id}`}
                    // showUiContrast={showContrast === `showUiContrast-${id}`}
                    title='Black'
                    id='black'
                  />
                </>
              )}

              {rampColors.map((color: Record<string, any>, colorIdx: number) => (
                <Swatch
                  key={colorIdx}
                  rootName={rootName}
                  colorIdx={rampColors.length - 1 - colorIdx}
                  colors={rampColors}
                  // showTextContrast={showContrast === `showTextContrast-${id}`}
                  // showUiContrast={showContrast === `showUiContrast-${id}`}
                  hexDisabled={rampColors.length > 1}
                  id={`${rampColors.length - colorIdx}0`}
                />
              ))}
              {rampColors.length > 1 && (
                <>
                  <ViewOnlySwatch
                    color={base_colors.white}
                    colors={rampColors}
                    // showTextContrast={showContrast === `showTextContrast-${id}`}
                    // showUiContrast={showContrast === `showUiContrast-${id}`}
                    title='White'
                    id='white'
                  />
                  <ViewOnlySwatch
                    color={base_colors.gray_100}
                    colors={rampColors}
                    // showTextContrast={showContrast === `showTextContrast-${id}`}
                    // showUiContrast={showContrast === `showUiContrast-${id}`}
                    title='Gray 100'
                    id='gray_100'
                  />
                  <ViewOnlySwatch
                    color={base_colors.gray_90}
                    colors={rampColors}
                    // showTextContrast={showContrast === `showTextContrast-${id}`}
                    // showUiContrast={showContrast === `showUiContrast-${id}`}
                    title='Gray 90'
                    id='gray_90'
                  />
                  <ViewOnlySwatch
                    color={base_colors.gray_10}
                    colors={rampColors}
                    // showTextContrast={showContrast === `showTextContrast-${id}`}
                    // showUiContrast={showContrast === `showUiContrast-${id}`}
                    title='Gray 10'
                    id='gray_10'
                  />
                </>
              )}
            </>
          )}
        </div>
        <div className={styles.ramp__footer}>
          <Button theme='primary' variant='rounded' type='button' icon='add_regular' onClick={addRamp} />
          <Button theme='primary' variant='rounded' icon='trash' type='button' onClick={removeRamp} />

          {rampColors.length === 1 && rampColors[0].hex !== '#ffffff' && (
            <Button
              label='Generate Ramp'
              variant='rounded'
              className={styles.rampBtn}
              onClick={() => generateColors(rampColors[0], setFieldValue)}
            />
          )}
        </div>
      </div>
    </ColorRampProvider>
  );
};
export default ColorRampField;
